export const DEMO_NAME_PREFIX = 'ZZ - DEMO - '

/**
 * Default benefit effective date: the 1st of the next calendar month.
 * This ensures the date stays in the near future regardless of when the
 * form is opened, and avoids hardcoding a specific year.
 */
function getDefaultBenefitEffectiveDate(): string {
  const d = new Date()
  // Advance to the 1st of next month using local calendar values so the
  // result is correct in all timezones (toISOString is UTC and would shift
  // the date back one day for UTC+ users).
  const year = d.getFullYear()
  const month = d.getMonth() + 2 // next month, 1-based
  const adjustedYear = month > 12 ? year + 1 : year
  const adjustedMonth = month > 12 ? 1 : month
  return `${adjustedYear}-${String(adjustedMonth).padStart(2, '0')}-01`
}

export const DEFAULT_BENEFIT_EFFECTIVE_DATE = getDefaultBenefitEffectiveDate()

export const PAYROLL_CYCLE_OPTIONS = [
  { label: 'Monthly (12)', value: '12' },
  { label: 'Semi-Monthly (24)', value: '24' },
  { label: 'Bi-Weekly (26)', value: '26' },
  { label: 'Weekly (52)', value: '52' },
]

interface PresetItem {
  label: string
  value: string
  description?: string
}

interface PresetSection {
  title: string
  items: PresetItem[]
}

/**
 * DEMO_PRESETS is the source-of-truth manifest for every setting the backend
 * automatically applies when creating a demo customer. It is rendered
 * read-only in the presets modal so Sales users know what gets configured.
 */
export const DEMO_PRESETS: PresetSection[] = [
  {
    title: 'Company Configuration',
    items: [
      {
        label: 'Customer Status',
        value: 'DRAFT',
        description: 'Company starts in Draft so it is never treated as live.',
      },
      {
        label: 'All Emails',
        value: 'OFF',
        description:
          'ICHRA Notices, Welcome, Reminder, Enrollment Confirmation, and Application Verified emails are all disabled to prevent inadvertent sends.',
      },
      {
        label: 'CMv1 Navigation Link',
        value: 'OFF',
        description:
          'HR Admin cannot access or modify company/member setup via CMv1.',
      },
      {
        label: 'CMv2 Navigation Link',
        value: 'OFF',
        description:
          'HR Admin cannot access or modify company/member setup via CMv2.',
      },
      {
        label: 'Display ER Contributions',
        value: 'OFF',
        description: 'Employer contributions are hidden from employees.',
      },
      {
        label: 'New Shop For Health (SFH)',
        value: 'ON',
        description: 'Uses the updated SFH experience.',
      },
      {
        label: 'SFH Landing Page',
        value: 'Browsing Options Page',
        description: 'Employees land directly on the plan browsing view.',
      },
      {
        label: 'Provider Lookup',
        value: 'ON',
        description: 'Provider search is enabled in SFH.',
      },
      {
        label: 'Open Enrollment Tab',
        value: 'All toggles ON',
        description:
          'showOpenEnrollmentTab, showPlansSubTab, and showPeopleSubTab are all enabled.',
      },
      {
        label: 'isTestClient',
        value: 'TRUE',
        description:
          'Marks the company as a test client so it is excluded from reports and daily dashboards.',
      },
    ],
  },
  {
    title: 'HR Admin Portal Restrictions',
    items: [
      {
        label: 'Accessible sections',
        value:
          'Home, Enrollment, Open Enrollment (ABP), Our People, Coverage Summary',
        description: 'Only these areas are visible to the demo HR Admin.',
      },
      {
        label: 'Add Worker',
        value: 'HIDDEN',
        description: '"Add Worker" option removed from Our People.',
      },
      {
        label: 'Edit Worker / Stop Coverage',
        value: 'HIDDEN',
        description:
          'Actions dropdown on Our People and Coverage Summary pages has these options removed.',
      },
      {
        label: 'Profile link (left nav)',
        value: 'HIDDEN',
        description: 'Prevents HR Admin from changing their password via nav.',
      },
      {
        label: 'Enrollment Dashboard actions',
        value:
          'Send Reminder, Send ICHRA, Re-send Reminder, Re-send Welcome Email — all HIDDEN',
        description:
          'These email-triggering actions are hidden from the demo HR Admin.',
      },
    ],
  },
  {
    title: 'Employee Population',
    items: [
      {
        label: 'Source',
        value: 'Sanitized prospect quote file (Shoppable tab)',
        description:
          'Employee data is derived from the uploaded quote XLSX. PHI is sanitized before import.',
      },
      {
        label: 'IsTestUser',
        value: 'TRUE on all employees',
        description:
          'Prevents elections from appearing as applications in App Engine and excludes employees from certain reports.',
      },
      {
        label: 'HireDate',
        value: '3 months prior to demo creation date',
      },
      {
        label: 'Defaults',
        value:
          'Smoker=false, IsExempt=true, MaritalStatus=OTHER, Salary=50000, EmploymentStatus=FULL-TIME',
      },
    ],
  },
  {
    title: 'Pre-registered Accounts',
    items: [
      {
        label: 'Employee account',
        value: '1 pre-registered EE (username + password ready)',
        description: 'Ready to enroll — no self-registration required.',
      },
      {
        label: 'HR Admin account',
        value: '1 pre-registered HR Admin (username + password ready)',
        description:
          'ToS pre-accepted. Spoofing / "Login as Worker" enabled for this account.',
      },
    ],
  },
]
